/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Stack, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DetailsIcon from '../../../../../../../../../assets/Resources/DetailIcon.svg';
import CategoriesIcon from '../../../../../../../../../assets/Resources/CategoriesIcon.svg';
import Playlist from '../../../../../../../../../assets/SvgIcons/resources/playlist.svg';
import AcessIcon from '../../../../../../../../../assets/Resources/AccessIcon.svg';
import { IoPricetagOutline } from 'react-icons/io5';
import { FaRegEdit } from 'react-icons/fa';
import { PiClockCountdownBold } from 'react-icons/pi';
import { LanguageContext } from '../../../../../../../../../libs/Context';
// import { VideoItem } from '../VideoDragList';

// Import components from Reusable folder with correct ES6 imports
import DetailsSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/DetailsSlider';
import PlaylistSlider from '../../../Reusable/RightBarOptions/Components/SliderContent/Components/PlaylistSlider/PlaylistSlider';
import CategorySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/CategorySlider';
import TagSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/TagsSlider';
import AccessSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/AccessSlider';
import ManageContentSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ManageContentSlider';
import ActivitySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ActivitySlider';
import { VideoItem } from '../types';
import useContentPermissions from '../../../Reusable/ContentCards/useContentPermissions';
import { selectedTabs } from '../../../../../constants';

interface VideoDetailsSliderProps {
  selectedTab?: selectedTabs;
  setSelectedTab: (tab: selectedTabs) => void;
  setOpen: (open: boolean) => void;
  articleCreate?: string;
  videoData?: any;
  selectedItems?: VideoItem[]; // Changed to VideoItem[]
  videosList?: VideoItem[];
  companyId?: string;
  onSave?: (updatedData: any, videoIds?: any) => void;
  requestFrom?: string;
}

// Define the access control data structure
interface AccessControlData {
  internalEmployees?: {
    allSelected?: boolean;
    departments?: Array<{name: string; count?: number}>;
    specificUsers?: Array<{userId: string; name: string; email: string; role?: string}>;
  };
  userGroups?: Array<{type: string; count?: number}>;
  participantCount?: number;
  participantLimit?: number;
}

// Define a consolidated UI state interface
interface VideoUIState {
  details: {
    title: string;
    description: string;
    thumbnail?: {
      url: string;
      canEdit?: boolean;
      canDelete?: boolean;
      source?: string;
      key?: string;
    };
  };
  playlists: any[]; // Array of playlist objects with complete data
  categories: string[];
  tags: string[];
  access: {
    visibility?: 'public' | 'private'|'';
    allowDownload?: boolean;
    allowShare?: boolean;
    accessControl?: AccessControlData;
  };
  manage: {
    quality?: string;
    status?: string;
    publishDate?: string;
  };
}

// Add a type for tab configuration
interface TabConfig {
  name: 'Details' | 'Categories' | 'Playlist' | 'Add Tags' | 'Tags' | 'Access' | 'Activity';
  Icon: any;
  ImageIcon: boolean;
  hideWhen?: string[]; // Array of requestFrom values when this tab should be hidden
}

/**
 * VideoDetailsSlider - A replica of DetailsOptionSlider but only for video content
 */
const VideoDetailsSlider: React.FC<VideoDetailsSliderProps> = (props): JSX.Element =>
{
	const {
		selectedTab = 'Details',
		setSelectedTab,
		setOpen,
		articleCreate,
		videoData, // when selecting a single video, this will be an object of video data
		selectedItems = [], // when selecting multiple videos, this will be an array of video items
		onSave,
		requestFrom
	} = props;

	const { language } = useContext(LanguageContext);
	const { isPrivateShareEnabled, isShareEnabled, isDownloadEnabled } = useContentPermissions();

	// Define all possible tabs with their conditions
	const allTabs: TabConfig[] = [
		{ name: 'Playlist', Icon: Playlist, ImageIcon: true },
		{ name: 'Categories', Icon: CategoriesIcon, ImageIcon: true, hideWhen: [ 'playlist' ] },
		{ name: 'Add Tags', Icon: <IoPricetagOutline fontSize={'26px'} />, ImageIcon: false, hideWhen: [ 'playlist' ] },
		{ name: 'Access', Icon: AcessIcon, ImageIcon: true, hideWhen: [ 'playlist' ] }
		// { name: 'Activity', Icon: <PiClockCountdownBold fontSize={'26px'} />, ImageIcon: false, hideWhen: [ 'playlist' ] }
	];

	// Define the tabs specific to video editing - memoized to prevent unnecessary re-creation
	const videoTabs = useMemo(() =>
	{
		// Filter tabs based on requestFrom
		const baseTabs = allTabs.filter((tab) =>
			!tab.hideWhen || // Always show tabs without hideWhen
			!(requestFrom && tab.hideWhen.includes(requestFrom)) // Hide tabs that match requestFrom
		);

		// Only add the Details tab if a single video is selected
		if (!selectedItems || selectedItems.length <= 1)
		{
			return [
				{ name: 'Details', Icon: DetailsIcon, ImageIcon: true },
				...baseTabs
			];
		}

		return baseTabs;
	}, [ selectedItems, requestFrom ]);

	// Update selectedTab if Details is selected but no longer available
	useEffect(() =>
	{
		if (selectedTab === 'Details' && selectedItems && selectedItems.length > 1)
		{
			// Switch to first available tab if Details is not available
			if (videoTabs.length > 0)
			{
				setSelectedTab(videoTabs[0].name as selectedTabs);
			}
		}
	}, [ selectedItems, selectedTab, videoTabs ]);

	// console.log(videoData, 'videoData');

	// Simple function to create initial state from video data or empty defaults
	const createInitialState = useCallback((): VideoUIState =>
	{
		return {
			details : {
				title       : videoData?.title || '',
				description : videoData?.description || '',
				thumbnail   : videoData?.thumbnail || ''
			},
			playlists  : videoData?.playlists || [],
			categories : videoData?.categories || [],
			tags       : videoData?.tags || [],
			access     : {
				// visibility    : videoData?.visibility !== undefined ? (videoData?.visibility?.toLowerCase() as 'public' | 'private') : isPrivateShareEnabled ? 'private' : 'public',
				allowDownload : videoData?.allowDownload !== undefined ? videoData?.allowDownload : isDownloadEnabled,
				allowShare    : videoData?.allowShare !== undefined ? videoData?.allowShare : isShareEnabled
				// accessControl : videoData?.accessControl || ''
			},
			manage : {
				quality     : videoData?.quality || 'Standard',
				status      : videoData?.status || '',
				publishDate : videoData?.publishDate || ''
			}
		};
	}, [ videoData ]);

	// Store original state for comparison
	const [ originalData, setOriginalData ] = useState<VideoUIState | null>(null);

	// Simplified state management - just use the video data or empty defaults
	const [ uiState, setUiState ] = useState<VideoUIState>(createInitialState);

	// Reset state when selection changes
	useEffect(() =>
	{
		const newState = createInitialState();

		setUiState(newState);
		setOriginalData(newState);
	}, [ videoData ]);

	// Detect changes in state
	const hasChanges = useMemo(() =>
	{
		if (!originalData) return false;

		// Simple check for most common fields that might change
		if (
			originalData.details.title !== uiState.details.title ||
			originalData.details.description !== uiState.details.description ||
			JSON.stringify(originalData.categories) !== JSON.stringify(uiState.categories) ||
			JSON.stringify(originalData.playlists) !== JSON.stringify(uiState.playlists) ||
			JSON.stringify(originalData.tags) !== JSON.stringify(uiState.tags) ||
			originalData.details.thumbnail?.url !== uiState.details.thumbnail?.url ||
			// originalData.access.visibility !== uiState.access.visibility ||
			originalData.access.allowDownload !== uiState.access.allowDownload ||
			originalData.access.allowShare !== uiState.access.allowShare
		)
		{
			return true;
		}

		// Deep comparison for more complex objects
		return JSON.stringify(originalData) !== JSON.stringify(uiState);
	}, [ originalData, uiState ]);

	// Enhanced updateState to support functional updates for sections
	const updateState = useCallback(<K extends keyof VideoUIState>(
		section: K,
		value: VideoUIState[K] | ((prev: VideoUIState[K]) => VideoUIState[K])
	) =>
	{
		setUiState((prev) =>
		{
			const newValue = typeof value === 'function'
				? (value as Function)(prev[section])
				: value;

			return {
				...prev,
				[section] : newValue
			};
		});
	}, []);

	// Specialized handlers for specific state updates
	const handleDetailsUpdate = useCallback((details: any) =>
	{
		updateState('details', (prev) => ({ ...prev, ...details }));
	}, [ updateState ]);

	const handleCategoriesUpdate = useCallback((categories: string[]) =>
	{
		updateState('categories', categories);
	}, [ updateState ]);

	// Handle updates to tags
	const handleTagsUpdate = useCallback((tags: string[]) =>
	{
		updateState('tags', tags);
	}, [ updateState ]);

	const handlePlaylistUpdate = useCallback((playlistData: any | any[]) =>
	{
		updateState('playlists', (prev) =>
		{
			// Handle array of playlists from tree view selection
			if (Array.isArray(playlistData))
			{
				// Keep existing playlists that are still selected
				const updatedPlaylists = playlistData.map((playlist, index) => ({
					playlistId      : playlist.playlistId || playlist._id,
					orderInPlaylist : playlist.orderInPlaylist || index
				}));

				return updatedPlaylists;
			}
			console.log(playlistData);

			// Handle single playlist from creation
			const playlist = playlistData;

			if (!playlist || !playlist._id)
			{
				console.error('Invalid playlist object received:', playlist);

				return prev;
			}

			// Check if playlist already exists
			const playlistExists = prev.some((p) => p.playlistId === playlist._id);

			console.log(playlistExists);

			if (!playlistExists)
			{
				return [ ...prev, {
					playlistId      : playlist._id,
					orderInPlaylist : 0
				} ];
			}

			return prev;
		});
	}, [ updateState ]);

	// Handle updates to access control settings, merging changes and only updating if different
	const handleAccessUpdate = useCallback((accessData: Partial<VideoUIState['access']>) =>
	{
		updateState('access', (prev) =>
		{
			const merged = { ...prev, ...accessData };

			if (JSON.stringify(prev) === JSON.stringify(merged))
			{
				return prev;
			}

			return merged;
		});
	}, [ updateState ]);

	// Enhanced save function to handle multiple videos
	const handleSave = useCallback(() =>
	{
		// Ensure visibility is either 'public' or 'private'
		// let validVisibility: 'public' | 'private' | ''| undefined;

		// if (uiState.access.visibility && [ 'public', 'private' ].includes(uiState.access.visibility?.toLowerCase()))
		// {
		// 	validVisibility = uiState.access.visibility?.toLowerCase() as 'public' | 'private';
		// }
		// else
		// {
		// 	// Default to 'public' if invalid value
		// 	validVisibility = '';
		// }

		// Format playlists for API - convert to required format and filter out incomplete entries
		const formattedPlaylists = uiState.playlists
			.filter((playlist: any) => playlist?.playlistId && playlist.orderInPlaylist !== undefined)
			.map((playlist: any) => ({
				playlistId      : playlist?.playlistId,
				orderInPlaylist : playlist.orderInPlaylist
			}));

		// Calculate updated participant count for access control
		let participantCount = 0;
		const accessControl = uiState.access.accessControl;

		if (accessControl)
		{
			// Count internal employees
			if (accessControl.internalEmployees)
			{
				if (accessControl.internalEmployees.allSelected)
				{
					// If all employees are selected, we would need actual count here
					participantCount += accessControl.internalEmployees?.departments?.reduce((sum, dept) => sum + (dept.count || 0), 0) || 0;
				}
				else
				{
					// Count departments
					participantCount += accessControl.internalEmployees?.departments?.reduce((sum, dept) => sum + (dept.count || 0), 0) || 0;
					// Count specific users
					participantCount += accessControl.internalEmployees?.specificUsers?.length || 0;
				}
			}

			// Count user groups
			participantCount += accessControl.userGroups?.reduce((sum, group) => sum + (group.count || 0), 0) || 0;

			// Update the participant count
			accessControl.participantCount = participantCount;
		}

		// Create updated data object based on whether we're editing a single or multiple videos
		const updatedVideoData: Partial<VideoItem> = {};

		if (selectedItems && selectedItems.length > 1)
		{
			// For multiple videos - create base object without categoryIds
			const baseData: any = {};

			if (formattedPlaylists.length > 0)
			{
				baseData.playlists = formattedPlaylists;
			}

			// Include tags if specified
			if (uiState.tags.length > 0)
			{
				baseData.tags = uiState.tags;
			}

			// Always include access control settings
			baseData.allowDownload = uiState.access.allowDownload;
			baseData.allowShare = uiState.access.allowShare;

			// Only include categoryIds if we're on the Categories tab
			// if (uiState.categories.length > 0)
			// {
			// videoData?.categories[0] !== uiState?.categories[0] && (updatedVideoData.categories = uiState.categories);
			// baseData?.categoryIds[0] !== uiState?.categories[0] && 
			(baseData.categoryIds = uiState.categories);
			// }

			Object.assign(updatedVideoData, baseData);
		}
		else
		{
			// For single video - create base object without categoryIds
			const baseData: any = {};

			if (formattedPlaylists.length > 0)
			{
				baseData.playlists = formattedPlaylists;
			}

			// For single video, include all fields except categoryIds
			baseData.title = uiState.details.title;
			baseData.description = uiState.details.description;
			baseData.thumbnail = uiState.details.thumbnail;
			baseData.tags = uiState.tags;
			baseData.allowDownload = uiState.access.allowDownload;
			baseData.allowShare = uiState.access.allowShare;
			baseData.quality = uiState.manage.quality;
			baseData.status = uiState.manage.status;
			baseData.publishDate = uiState.manage.publishDate;

			// Only include categoryIds if we're on the Categories tab

			if (uiState.categories.length > 0 && baseData?.categoryIds?.[0] !== uiState?.categories?.[0])
			{
				// videoData?.categories[0] !== uiState?.categories[0] && (updatedVideoData.categories = uiState.categories);
				baseData.categoryIds = uiState.categories;
			}

			Object.assign(updatedVideoData, baseData);
		}

		// Remove undefined fields to avoid accidentally clearing data
		Object.keys(updatedVideoData).forEach((key) =>
		{
			if (updatedVideoData[key as keyof typeof updatedVideoData] === undefined)
			{
				delete updatedVideoData[key as keyof typeof updatedVideoData];
			}
		});

		// Send data back to parent component
		if (onSave)
		{
			if (selectedItems && selectedItems.length > 0)
			{
				// Extract IDs for the save operation

				onSave(updatedVideoData, selectedItems);
			}
			else if (videoData)
			{
				onSave(updatedVideoData, [ videoData.id ? videoData.id : videoData?._id ]);
			}
		}

		// Close the slider
		setOpen(false);
	}, [ uiState, onSave, setOpen, videoData, selectedItems, selectedTab ]);

	// console.log(uiState);

	// Memoize the tab content to prevent unnecessary re-renders
	const renderTabContent = useMemo(() =>
	{
		switch (selectedTab)
		{
			case 'Details':
				// Only render Details content if a single video is selected
				if (selectedItems && selectedItems.length > 1)
				{
					return null;
				}

				return (
					<DetailsSlider
						articleCreate={articleCreate}
						data={uiState.details}
						itemType='video'
						onChange={handleDetailsUpdate}
					/>
				);
			case 'Categories':
				return (
					<CategorySlider
						artcileCreate={articleCreate}
						categories={uiState.categories}
						onChange={handleCategoriesUpdate}
					/>
				);
			case 'Playlist':
				return (
					<PlaylistSlider
						onChange={handlePlaylistUpdate}
						data={{
							name        : '',
							description : ''
						}}
						selectedPlaylists={uiState.playlists}
					/>
				);
			case 'Add Tags':
			// case 'Tags':
				return (
					<TagSlider
						tags={videoData?.tags || []}
						initialSelectedTags={uiState.tags}
						onChange={handleTagsUpdate}
					/>
				);
			case 'Access':
				return (
					<AccessSlider
						visibility={uiState.access.visibility}
						allowDownload={uiState.access.allowDownload}
						allowShare={uiState.access.allowShare}
						accessControl={uiState.access.accessControl}
						onChange={handleAccessUpdate}
					/>
				);
				//   case 'Manage':
				//     return (
				//       <ManageContentSlider
				//         // When implemented, these props would be needed:
				//         // manage={uiState.manage}
				//         // onChange={(updatedManage) => updateState('manage', prev => ({ ...prev, ...updatedManage }))}
				//       />
				//     );
			default:
				return null;
		}
	}, [ selectedTab, uiState, articleCreate, videoData, handleDetailsUpdate, handleCategoriesUpdate, handlePlaylistUpdate, handleTagsUpdate, handleAccessUpdate ]);

	return (
		<Grid container height='100%' width='100%'>
			{/* Left Sidebar Tabs */}
			<Grid item xs={2.5} height={'100%'} sx={{ boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.10)' }}>
				<Stack spacing={2} pt={3}>
					{videoTabs.map((tab, index) => (
						<Stack
							key={index}
							direction='column'
							alignItems='center'
							sx={{
								cursor          : 'pointer',
								padding         : '10px 8px',
								borderRight     : selectedTab === tab.name ? '4px solid #24a4c4' : '4px solid transparent',
								backgroundColor : selectedTab === tab.name ? '#EFFBFF' : ''
							}}
							onClick={() => setSelectedTab(tab.name as selectedTabs)}
						>
							{tab?.ImageIcon
								? (
									<Box width={'28px'} height={'28px'}>
										<img src={`${tab.Icon}`} alt='' className='invertGray' />
									</Box>
								)
								: (tab.Icon)}
							<Typography variant='body2' color={'#737373'} pt={0.2}>{tab.name}</Typography>
						</Stack>
					))}
				</Stack>
			</Grid>

			{/* Right Content Area */}
			<Grid item xs={9.5} height={'100%'} width={'100%'}>
				<Grid container height={'100%'} direction={'column'} display={'flex'}>
					<Grid item px={2} py={1}>
						<Stack direction={'row'} alignItems={'center'} display={'flex'} justifyContent={'space-between'}>
							<Box>
								<Typography fontSize={'22px'} fontWeight={'var(--font500)'}>{selectedTab}</Typography>
							</Box>
							<IconButton onClick={() => setOpen(false)}>
								<CloseIcon />
							</IconButton>
						</Stack>
					</Grid>
					<Grid
						item
						minHeight={0}
						pt={1}
						px={2}
						pb={1}
						xs
						sx={{ overflowY: 'auto' }}
						onClick={(e) => e.stopPropagation()} // Stop event propagation
					>
						{renderTabContent}
					</Grid>
					{selectedTab !== 'Activity' && (
						<Grid item>
							<Stack direction={'row'} p={2} spacing={2} alignItems={'center'} justifyContent={'end'} display={'flex'}>
								<Button onClick={() => setOpen(false)} variant='outlined'>{language?.cancel}</Button>
								<Button
									variant='contained'
									sx={{ color: 'var(--whiteColor)' }}
									onClick={handleSave}
									//   disabled={!hasChanges}
								>
									{language?.save}
								</Button>
							</Stack>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

// Use React.memo to prevent unnecessary re-renders of the entire component
export default React.memo(VideoDetailsSlider);